import { useEffect, useState } from "react";
import { Plus, Trash2, Power, Loader2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, EmptyState } from "@/components/dash";
import CoinIcon from "@/components/CoinIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { apiErr } from "@/lib/api";
import { truncate } from "@/lib/format";

const NETWORKS = {
  BTC: [["BTC", "Bitcoin"]],
  ETH: [["ERC20", "Ethereum (ERC20)"]],
  USDT: [["TRC20", "Tron (TRC20)"], ["ERC20", "Ethereum (ERC20)"]],
  USDC: [["ERC20", "Ethereum (ERC20)"], ["SOL", "Solana"]],
  SOL: [["SOL", "Solana"]],
};

export default function AdminCommissionWallets() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ coin: "USDT", network: "TRC20", address: "", active: true });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/commission-wallets").then(({ data }) => setItems(data.wallets)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post("/admin/commission-wallets", form); toast.success("Komisyon cüzdanı eklendi."); setOpen(false); setForm({ coin: "USDT", network: "TRC20", address: "", active: true }); load(); }
    catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const toggle = async (w) => { await api.patch(`/admin/commission-wallets/${w.id}`, { active: !w.active }); load(); };
  const del = async (id) => { await api.delete(`/admin/commission-wallets/${id}`); toast.success("Silindi."); load(); };

  if (!items) return <Spinner />;
  return (
    <div>
      <PageHeader title="Komisyon Cüzdanları" desc="Her coin + ağ için komisyonların yönlendirileceği cüzdanlar. Sabit adres kullanılmaz."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-cw-btn" className="rounded-full"><Plus size={16} /> Cüzdan Ekle</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Komisyon Cüzdanı</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div><Label className="text-sm">Coin</Label>
                  <Select value={form.coin} onValueChange={(v) => setForm((f) => ({ ...f, coin: v, network: NETWORKS[v][0][0] }))}>
                    <SelectTrigger className="mt-1.5" data-testid="cw-coin"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.keys(NETWORKS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-sm">Network</Label>
                  <Select value={form.network} onValueChange={(v) => setForm((f) => ({ ...f, network: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="cw-network"><SelectValue /></SelectTrigger>
                    <SelectContent>{NETWORKS[form.coin].map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-sm">Cüzdan Adresi</Label><Input required value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="cw-address" className="mt-1.5 font-mono text-xs" /></div>
                <Button type="submit" disabled={saving} className="w-full rounded-full">{saving ? <Loader2 className="animate-spin" size={16} /> : "Ekle"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      {items.length === 0 ? <EmptyState icon={Wallet} title="Komisyon cüzdanı yok" desc="Komisyonların yönlendirilmesi için her coin/ağ ekleyin." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cw-grid">
          {items.map((w) => (
            <Card key={w.id} className="p-5">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 font-bold"><CoinIcon symbol={w.coin} size={26} /> {w.coin} · {w.network}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${w.active ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground"}`}>{w.active ? "Aktif" : "Pasif"}</span>
              </div>
              <p className="mt-3 font-mono text-xs text-muted-foreground break-all">{truncate(w.address, 16)}</p>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1 rounded-lg" onClick={() => toggle(w)}><Power size={14} /> {w.active ? "Pasifleştir" : "Aktifleştir"}</Button>
                <Button size="sm" variant="outline" className="rounded-lg text-red-500" onClick={() => del(w.id)}><Trash2 size={14} /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
